import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import LocalOpsSheet from './LocalOpsSheet';
import LocalOpsFeedItem from './LocalOpsFeedItem';
import { formatRelativeTime } from '../../utils/localOpsTime';
import JGLocalOpsMark from '../common/JGLocalOpsMark';

const ITEM_HEIGHT = 76;
const BUFFER = 4;

export default function LocalOpsFeed({
  events = [],
  open,
  onClose,
  title = 'LocalOps Live',
  description = 'Live activity from nearby locals.',
  variant = 'sheet',
  onSelectEvent,
}) {
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const rafRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(360);
  const now = Date.now();
  const isEmpty = events.length === 0;

  const handleScroll = useCallback((event) => {
    const nextTop = event.currentTarget.scrollTop;
    if (typeof window === 'undefined') {
      setScrollTop(nextTop);
      return;
    }
    if (rafRef.current) return;
    rafRef.current = window.requestAnimationFrame(() => {
      setScrollTop(nextTop);
      rafRef.current = null;
    });
  }, []);

  useLayoutEffect(() => {
    const node = listRef.current;
    if (!node) return;
    const measure = () => setViewportHeight(node.clientHeight || 360);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open]);

  useEffect(() => {
    if (variant !== 'peek' || !open || !onClose) return;
    const handlePointerDown = (event) => {
      const node = containerRef.current;
      if (!node || node.contains(event.target)) return;
      onClose();
    };
    window.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      window.removeEventListener('pointerdown', handlePointerDown, true);
  }, [variant, open, onClose]);

  const shouldVirtualize = events.length > 20;
  const total = events.length;
  const startIndex = shouldVirtualize
    ? Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - BUFFER)
    : 0;
  const visibleCount = shouldVirtualize
    ? Math.ceil(viewportHeight / ITEM_HEIGHT) + BUFFER * 2
    : total;
  const endIndex = shouldVirtualize
    ? Math.min(total, startIndex + visibleCount)
    : total;
  const visibleEvents = useMemo(
    () => events.slice(startIndex, endIndex),
    [events, startIndex, endIndex],
  );
  const paddingTop = shouldVirtualize ? startIndex * ITEM_HEIGHT : 0;
  const paddingBottom = shouldVirtualize
    ? Math.max(0, total - endIndex) * ITEM_HEIGHT
    : 0;

  const skeletonRows = variant === 'peek' ? 2 : 3;
  const content = (
    <div
      className={`localops-feed localops-feed--${variant} ${
        isEmpty ? 'is-empty' : ''
      }`}
    >
      <div className="localops-feed-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <JGLocalOpsMark />
          <div>
            <div className="localops-feed-title">{title}</div>
            <div className="localops-feed-sub">{description}</div>
          </div>
        </div>
        {variant === 'peek' && onClose ? (
          <button
            type="button"
            className="localops-feed-close"
            onClick={onClose}
            aria-label="Close LocalOps Live"
          >
            Close
          </button>
        ) : null}
      </div>
      <div
        className="localops-feed-list"
        ref={listRef}
        onScroll={handleScroll}
      >
        <div style={{ paddingTop, paddingBottom }}>
          {visibleEvents.length ? (
            visibleEvents.map((event) => {
              const timeAgoLabel = formatRelativeTime(event.createdAt, now);
              const isNew = now - (event.createdAt || now) < 15000;
              return (
                <LocalOpsFeedItem
                  key={event.id}
                  event={{ ...event, isNew }}
                  timeAgoLabel={timeAgoLabel}
                  variant={variant === 'display' ? 'display' : 'default'}
                  onSelect={onSelectEvent}
                />
              );
            })
          ) : (
            <div className="localops-feed-empty">
              <div className="localops-feed-skeleton">
                {Array.from({ length: skeletonRows }).map((_, index) => (
                  <div
                    key={`localops-feed-skeleton-${index}`}
                    className="localops-feed-skeleton-row"
                  >
                    <div className="localops-skeleton localops-skeleton-avatar" />
                    <div className="localops-feed-skeleton-lines">
                      <span className="localops-skeleton localops-skeleton-line is-short" />
                      <span className="localops-skeleton localops-skeleton-line" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="localops-feed-empty-text">
                Activity will appear here as locals jump in.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (variant === 'sheet') {
    return (
      <LocalOpsSheet open={open} onClose={onClose} title={title}>
        {content}
      </LocalOpsSheet>
    );
  }

  if (variant === 'peek') {
    if (!open) return null;
    return (
      <div
        ref={containerRef}
        className="localops-feed-peek is-open"
        role="dialog"
        aria-label={title}
      >
        <div className="localops-feed-peek__handle" />
        {content}
      </div>
    );
  }

  return content;
}
